import { AnimatePresence, motion } from 'framer-motion';
import { Bell, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { AlertRow } from '@/components/alerts/alert-row';
import { EmptyState } from '@/components/common/empty-state';
import { useAcknowledgeAlert, useAlerts } from '@/lib/queries/alerts';
import { useUiStore } from '@/stores/ui-store';

export function NotificationBell() {
  const navigate = useNavigate();
  const { data, isLoading } = useAlerts({ status: 'OPEN', limit: 8, sortBy: 'occurredAt' });
  const acknowledge = useAcknowledgeAlert();
  const alertSound = useUiStore((s) => s.alertSound);
  const setAlertSound = useUiStore((s) => s.setAlertSound);

  const unread = data?.total ?? 0;
  const alerts = data?.items ?? [];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative size-8" aria-label="Notifications">
          <Bell className="size-4" />
          <AnimatePresence>
            {unread > 0 && (
              <motion.span
                initial={{ scale: 0.4, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.4, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 520, damping: 24 }}
                className="tnum absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-alert px-1 text-[9px] font-semibold text-white"
              >
                {unread > 99 ? '99+' : unread}
              </motion.span>
            )}
          </AnimatePresence>
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-[380px] p-0">
        <div className="flex items-center justify-between px-3 py-2.5">
          <p className="label-micro">Open alerts</p>
          <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-muted-foreground">
            <input
              type="checkbox"
              checked={alertSound}
              onChange={(event) => setAlertSound(event.target.checked)}
              className="size-3 accent-[var(--primary)]"
            />
            Sound
          </label>
        </div>
        <Separator />

        <div className="max-h-[340px] overflow-y-auto overscroll-contain">
          {isLoading ? (
            <div className="space-y-2 p-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="h-14 animate-pulse rounded-md bg-muted" />
              ))}
            </div>
          ) : alerts.length === 0 ? (
            <EmptyState
              icon={Check}
              title="All clear"
              description="No open alerts across the fleet."
              compact
              className="m-3 border-none"
            />
          ) : (
            <ul className="divide-y">
              <AnimatePresence initial={false}>
                {alerts.map((alert) => (
                  <motion.li
                    key={alert.id}
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
                  >
                    <AlertRow
                      alert={alert}
                      compact
                      onAcknowledge={() => acknowledge.mutate(alert.id)}
                    />
                  </motion.li>
                ))}
              </AnimatePresence>
            </ul>
          )}
        </div>

        <Separator />
        <div className="p-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-center text-xs"
            onClick={() => navigate('/alerts')}
          >
            Open alerts centre
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
